// Adaptive auto-scroll helpers for chat surfaces (WCAG 1.4.10 / 400% zoom).
//
// At high browser zoom the *document* can become the vertical scroll owner —
// the page grows past the viewport while the internal message list no longer
// scrolls (its flex height collapses to fit its content). scrollIntoView() is
// avoided on purpose: it scrolls EVERY scrollable ancestor, which yanked the
// header/composer out of view at 400% zoom. Instead we detect the actual
// scroll owner and drive it directly with scrollTo().

/** True when the container itself has vertical overflow to scroll. */
export function containerScrolls(container: HTMLElement): boolean {
  return container.scrollHeight - container.clientHeight > 1;
}

/**
 * True when the reader is within `threshold` px of the newest messages, in
 * whichever element currently owns vertical scrolling (container or document).
 */
export function isNearBottom(container: HTMLElement, threshold = 120): boolean {
  if (containerScrolls(container)) {
    return container.scrollHeight - container.scrollTop - container.clientHeight < threshold;
  }
  const doc = document.documentElement;
  return doc.scrollHeight - window.scrollY - window.innerHeight < threshold;
}

/**
 * Scroll the correct owner so the START of `anchor` (the latest message) is
 * visible — never leaving the viewport parked on the composer. The target is
 * clamped to max scroll, so short trailing messages land bottom-aligned
 * (classic scroll-to-bottom) while long replies show their first line at the
 * top with `margin` px of breathing room. With no anchor, scrolls to the end.
 */
export function scrollAnchorIntoView(
  container: HTMLElement,
  anchor: HTMLElement | null,
  reduceMotion: boolean,
  margin = 12,
): void {
  const behavior: ScrollBehavior = reduceMotion ? "auto" : "smooth";
  if (containerScrolls(container)) {
    const max = container.scrollHeight - container.clientHeight;
    const target = anchor
      ? container.scrollTop +
        (anchor.getBoundingClientRect().top - container.getBoundingClientRect().top) -
        margin
      : max;
    container.scrollTo({ top: Math.min(Math.max(0, target), max), behavior });
    return;
  }
  // Document is the scroll owner (e.g. 300–400% zoom reflow).
  const doc = document.documentElement;
  const max = doc.scrollHeight - window.innerHeight;
  const top = anchor ? window.scrollY + anchor.getBoundingClientRect().top - margin : max;
  window.scrollTo({ top: Math.min(Math.max(0, top), max), behavior });
}
